"use client";

import { useEffect, useRef, useState } from "react";
import { useT } from "@/hooks/useT";

/**
 * HeroV3StatsRow — the 3 aggregate stats under the CTAs, but each value
 * counts up from 0 the moment the row scrolls into view.
 *
 * Why client: the count-up requires rAF + IntersectionObserver. The shell
 * is rendered on the server by the caller; this component only owns the
 * animated number cells so hydration cost stays minimal.
 *
 * Reduced-motion: skipped entirely — we jump straight to the final value.
 */
export default function HeroV3StatsRow({
  addressableLiquidityUsd,
  totalVolume,
}: {
  addressableLiquidityUsd?: number | null;
  totalVolume: number;
}) {
  const t = useT();
  const liquidity = Number(addressableLiquidityUsd) > 0 ? Number(addressableLiquidityUsd) : 0;

  const items: Array<{
    label: string;
    value: number;
    format: (n: number) => string;
    hint: string;
    staticValue?: string;
  }> = [
    liquidity > 0
      ? {
          label: t("home.stats.addressableLiquidity"),
          value: liquidity,
          format: formatBig,
          hint: t("home.stats.addressableHint"),
        }
      : {
          label: t("home.stats.acrossVenues"),
          value: 0,
          format: () => t("home.stats.venuesValue"),
          hint: t("home.stats.venuesHint"),
          staticValue: t("home.stats.venuesValue"),
        },
    {
      label: t("home.stats.avgExecution"),
      value: 0,
      format: () => "<1s",
      hint: t("home.stats.avgExecutionHint"),
      staticValue: "<1s",
    },
    {
      label: t("home.stats.totalVolume"),
      value: totalVolume,
      format: formatBig,
      hint: t("home.stats.totalVolumeHint"),
    },
  ];

  return (
    <div className="grid grid-cols-3 gap-3 lg:gap-5 max-w-xl">
      {items.map((it, i) => (
        <div key={it.label}>
          <div className="font-inter-tight text-[9px] sm:text-[10px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-1">
            {it.label}
          </div>
          <div className="font-inter-tight text-xl sm:text-[26px] font-semibold text-gray-900 dark:text-white leading-none tabular-nums">
            {it.staticValue ? (
              <AnimatedText text={it.staticValue} delayMs={i * 200} />
            ) : (
              <CountUp target={it.value} format={it.format} delayMs={i * 200} />
            )}
          </div>
          <div className="font-inter-tight text-[10px] sm:text-[11px] text-gray-500 dark:text-gray-400 mt-1 text-balance sm:whitespace-nowrap">
            {it.hint}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Count from 0 → target over a fixed window, triggered when the element
 * scrolls into view. Uses requestAnimationFrame + easeOutCubic for a
 * natural deceleration curve. Mutates DOM directly instead of setState
 * per frame so we don't thrash React.
 */
function CountUp({
  target,
  format,
  delayMs = 0,
}: {
  target: number;
  format: (n: number) => string;
  delayMs?: number;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const el = ref.current;
    if (!el) return;

    if (reduced) {
      el.textContent = format(target);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting || started) continue;
          setStarted(true);
          observer.disconnect();
          const delayTimer = setTimeout(() => {
            animateTo(el, target, 1500, format);
          }, delayMs);
          // Cleanup if component unmounts mid-delay
          return () => clearTimeout(delayTimer);
        }
      },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  return <span ref={ref}>{format(0)}</span>;
}

function animateTo(
  el: HTMLElement,
  to: number,
  duration: number,
  format: (n: number) => string
) {
  const start = performance.now();
  const step = (now: number) => {
    const t = Math.min(1, (now - start) / duration);
    const eased = 1 - Math.pow(1 - t, 3);
    const value = to * eased;
    el.textContent = format(value);
    if (t < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

/**
 * For non-numeric values (e.g. "<1s", "1 UI") we still want some
 * staged animation so the row feels cohesive. Fade + translate-up,
 * delayed the same as the numeric counters.
 */
function AnimatedText({ text, delayMs = 0 }: { text: string; delayMs?: number }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setVisible(true);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          observer.disconnect();
          setTimeout(() => setVisible(true), delayMs);
        }
      },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [delayMs]);

  return (
    <span
      ref={ref}
      className={`inline-block transition-all duration-500 ease-[cubic-bezier(0.2,0.65,0.3,1)] ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1"
      }`}
    >
      {text}
    </span>
  );
}

function formatBig(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "$0";
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${Math.round(n).toLocaleString()}`;
}
